<template>
  <div class="mgr-logs">
    <el-card class="box-card" shadow="never">
      <div slot="header" class="card-header">
        <span>访问日志</span>
        <div class="card-header-actions">
          <span class="tip">集合：access_logs</span>
          <el-button size="small" :loading="loading" @click="query">刷 新</el-button>
        </div>
      </div>

      <!-- 关键字段过滤 -->
      <div class="lg-filters">
        <el-select v-model="filters.service" size="small" class="f-s" @change="search">
          <el-option label="全部服务" value="" />
          <el-option label="诗词游戏" value="shicigameservice" />
          <el-option label="数据服务" value="dataservice" />
          <el-option label="飞天学习" value="feitianstudyservice" />
          <el-option label="自动化测试" value="autotestservice" />
        </el-select>
        <el-select v-model="filters.event" size="small" class="f-s" @change="search">
          <el-option label="全部事件" value="" />
          <el-option label="访问" value="access" />
          <el-option label="登录" value="login" />
        </el-select>
        <el-input v-model="filters.path" size="small" clearable placeholder="路径关键字" class="f-w" @keyup.enter.native="search" />
        <el-select v-model="filters.method" size="small" class="f-s" @change="search">
          <el-option label="全部方法" value="" />
          <el-option label="GET" value="GET" />
          <el-option label="POST" value="POST" />
          <el-option label="PUT" value="PUT" />
          <el-option label="DELETE" value="DELETE" />
        </el-select>
        <el-select v-model="filters.status" size="small" class="f-s" @change="search">
          <el-option label="全部状态" value="" />
          <el-option label="200" value="200" />
          <el-option label="401" value="401" />
          <el-option label="403" value="403" />
          <el-option label="404" value="404" />
          <el-option label="500" value="500" />
        </el-select>
        <el-input v-model="filters.user" size="small" clearable placeholder="用户 / 账号" class="f-w" @keyup.enter.native="search" />
        <el-date-picker
          v-model="filters.range"
          size="small"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="timestamp"
          class="f-d"
        />
        <el-button size="small" type="primary" icon="el-icon-search" :loading="loading" @click="search">查询</el-button>
        <el-button size="small" @click="resetFilters">重置</el-button>
      </div>

      <!-- 列表：与 bijimng/travelmng 一致使用原生表格（el-table 在当前 Vue 2.7 栈上不渲染行） -->
      <div class="lg-table-wrap" v-loading="loading">
        <table class="lg-table">
          <thead>
            <tr>
              <th class="c-time">时间</th>
              <th class="c-svc">服务</th>
              <th class="c-ev">事件</th>
              <th class="c-ip">来源IP / 归属地</th>
              <th class="c-method">方法</th>
              <th class="c-path">路径</th>
              <th class="c-status">状态</th>
              <th class="c-lat">耗时</th>
              <th class="c-user">用户</th>
              <th class="c-client">客户端</th>
              <th class="c-note">结果 / 说明</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in logs" :key="row.ts + '-' + idx">
              <td class="c-time" data-label="时间">{{ formatTime(row.ts) }}</td>
              <td class="c-svc" data-label="服务">{{ serviceLabel(row.service) }}</td>
              <td class="c-ev" data-label="事件">
                <span class="lg-tag" :class="row.event === 'login' ? 'lg-tag--login' : 'lg-tag--access'">
                  {{ row.event === 'login' ? '登录' : '访问' }}
                </span>
              </td>
              <td class="c-ip" data-label="来源IP">
                <div>{{ row.ip || '-' }}</div>
                <div v-if="regionText(row)" class="lg-sub">{{ regionText(row) }}</div>
              </td>
              <td class="c-method" data-label="方法">{{ row.method }}</td>
              <td class="c-path" data-label="路径" :title="(row.path || '') + (row.query ? '?' + row.query : '')">
                {{ row.path }}<span v-if="row.query" class="lg-query">?{{ row.query }}</span>
              </td>
              <td class="c-status" data-label="状态">
                <span :class="statusClass(row.status)">{{ row.status }}</span>
              </td>
              <td class="c-lat" data-label="耗时">{{ row.latencyMs }} ms</td>
              <td class="c-user" data-label="用户">{{ row.user || row.username || '-' }}</td>
              <td class="c-client" data-label="客户端" :title="row.ua || ''">{{ clientText(row) || '-' }}</td>
              <td class="c-note" data-label="结果 / 说明">
                <template v-if="row.event === 'login'">
                  <span :class="row.result === 'success' ? 'lg-ok' : 'lg-fail'">
                    {{ row.result === 'success' ? '成功' : '失败' }}
                  </span>
                  <span v-if="row.reason" class="lg-reason">{{ row.reason }}</span>
                </template>
                <span v-else-if="row.status >= 400 && row.error" class="lg-fail" :title="row.error">{{ row.error }}</span>
                <span v-else class="lg-muted">{{ row.referer || '' }}</span>
              </td>
            </tr>
            <tr v-if="!loading && logs.length === 0">
              <td colspan="11" class="lg-empty">暂无日志数据</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="lg-pager">
        <el-pagination
          background
          :layout="pagerLayout"
          :pager-count="isMobile ? 5 : 7"
          :total="total"
          :current-page.sync="page"
          :page-size.sync="pageSize"
          :page-sizes="[20, 50, 100]"
          @current-change="query"
          @size-change="onSizeChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios'
import { UseUrl } from '../../utils/config'

export default {
  name: 'ManagerLogs',
  data() {
    return {
      loading: false,
      logs: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: {
        service: '',
        event: '',
        method: '',
        status: '',
        user: '',
        path: '',
        range: []
      }
    }
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768
    },
    pagerLayout() {
      return this.isMobile ? 'total, prev, pager, next' : 'total, prev, pager, next, sizes'
    }
  },
  mounted() {
    this.query()
  },
  methods: {
    buildParams() {
      const f = this.filters
      const params = {
        page: this.page,
        pageSize: this.pageSize,
        service: f.service || '',
        event: f.event || '',
        method: f.method || '',
        status: f.status === '' ? 0 : Number(f.status),
        user: (f.user || '').trim(),
        path: (f.path || '').trim(),
        startTs: 0,
        endTs: 0
      }
      if (Array.isArray(f.range) && f.range.length === 2 && f.range[0] && f.range[1]) {
        params.startTs = Number(f.range[0])
        params.endTs = Number(f.range[1])
      }
      return params
    },
    async query() {
      this.loading = true
      try {
        const res = await axios.post(UseUrl + '/manager/log/list', this.buildParams())
        this.total = res.data?.total || 0
        this.logs = res.data?.list || []
      } catch (e) {
        this.$message.error('获取日志失败')
      } finally {
        this.loading = false
      }
    },
    search() {
      this.page = 1
      this.query()
    },
    onSizeChange() {
      this.page = 1
      this.query()
    },
    resetFilters() {
      this.filters = { service: '', event: '', method: '', status: '', user: '', path: '', range: [] }
      this.page = 1
      this.query()
    },
    serviceLabel(s) {
      const m = {
        shicigameservice: '诗词游戏',
        dataservice: '数据服务',
        feitianstudyservice: '飞天学习',
        autotestservice: '自动化测试'
      }
      return m[s] || s || '-'
    },
    // ipRegion 是 ip2region 原始串（如 "中国|江苏省|南京市|0|CN"），这里整理成可读文本
    regionText(row) {
      const raw = row.ipRegion
      if (!raw) return ''
      if (raw === '内网') return '内网'
      const parts = raw.split('|').filter(x => x && x !== '0')
      // 末尾的两位国别码（CN/US）与首段国家重复，去掉
      if (parts.length > 1 && /^[A-Z]{2}$/.test(parts[parts.length - 1])) parts.pop()
      return parts.join(' ')
    },
    clientText(row) {
      return [row.device, row.os, row.browser].filter(Boolean).join(' · ')
    },
    formatTime(ts) {
      if (!ts) return '-'
      return new Date(Number(ts)).toLocaleString('zh-CN')
    },
    statusClass(status) {
      if (status >= 500) return 'lg-status lg-status--err'
      if (status >= 400) return 'lg-status lg-status--warn'
      if (status >= 200 && status < 300) return 'lg-status lg-status--ok'
      return 'lg-status'
    }
  }
}
</script>

<style scoped>
.mgr-logs {
  max-width: 1200px;
  margin: 0 auto;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  font-weight: 600;
}

.card-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 400;
}

.tip {
  font-size: 13px;
  color: #909399;
}

.lg-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.lg-filters .f-s {
  width: 120px;
}

.lg-filters .f-w {
  width: 160px;
}

.lg-filters .f-d {
  width: 340px;
}

.lg-table-wrap {
  overflow-x: auto;
}

.lg-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.lg-table th {
  padding: 10px 12px;
  background: #f5f7fa;
  color: #5a6474;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid #ebeef5;
}

.lg-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #ebeef5;
  vertical-align: middle;
  white-space: nowrap;
}

.lg-table tbody tr:hover {
  background: #f5f7fa;
}

.c-path {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.c-client {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lg-query {
  color: #909399;
}

.lg-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}

.lg-empty {
  padding: 26px 0;
  text-align: center;
  color: #909399;
}

.lg-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;
}

.lg-tag--access {
  color: #409eff;
  background: #ecf5ff;
}

.lg-tag--login {
  color: #e6a23c;
  background: #fdf6ec;
}

.lg-status--ok {
  color: #67c23a;
}

.lg-status--warn {
  color: #e6a23c;
}

.lg-status--err {
  color: #f56c6c;
}

.lg-ok {
  color: #67c23a;
}

.lg-fail {
  color: #f56c6c;
}

.lg-reason {
  margin-left: 8px;
  color: #909399;
}

.lg-muted {
  color: #c0c4cc;
}

.lg-pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

@media (max-width: 600px) {
  .lg-filters .f-s,
  .lg-filters .f-w,
  .lg-filters .f-d {
    width: 100%;
  }
}
</style>
